<template>
  <div
    v-if="hasAccess(CO_BRANDED_PAGE_ACCESS, CO_BRANDED_PAGE_ACCESS_MANAGE)"
    class="personal-info-list co-branded-page-view co-branded-page-view--partner"
    ref="coBranded"
    style="position:relative"
  >
    <template>
      <b-loading
        :is-full-page="false"
        :active="isPreloading"
        :can-cancel="false"
      />
    </template>

    <section class="personal-info-list__item personal-info">
      <h1>Co-branded Page</h1>
      <validation-observer v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(submit)" >
          <div class="columns" v-if="!isPreloading">
            <div class="column is-10">
              <template v-if="!editable && getCountPages > 1">
                <b-field
                  label="Choose your co-branded page"
                  class="personal-info__field block-title"
                  custom-class="personal-info__field-label"
                />
                <b-select
                  placeholder="Select a name"
                  class="page-name-selector"
                  v-model="currentPageName"
                  @change.native="changePage"
                >
                  <option
                    v-for="option in pages"
                    :value="option.pageName"
                    :key="option.id"
                  >
                    {{ option.pageName }}
                  </option>
                </b-select>
              </template>

              <CoBrandedPagePageUrl
                :page-name.sync="params.pageName"
                :editable="editable"
                :count-of-pages="getCountPages"
                :category-url-actually="categoryUrlActually"
                :page-full-url="pageFullUrl"
                tooltip-text="The URL address of your co-branded page.
                  Allowed symbols for the editable, branded part of the URL:
                  numbers, A-Z, a-z, hyphen."
              />

              <CoBrandedPageLogoImage
                class="mt-35"
                :editable="editable"
                :logo.sync="params.logoUrl"
                :blob-image.sync="blobImage"
                :alt="logoAlt"
                :no-logo-image-url="null"
                :append-to="$refs.coBranded"
                :cropper-image-width="200"
                :cropper-image-height="60"
                tooltip-text='The logo that will appear on your co-branded page (optional).
                 Use your company logo as a brand element. To upload your logo, click
                 “CHANGE LOGO” and upload a file, if necessary, crop the uploaded image,
                 and click “DONE”.'
              />
            </div>

            <div class="buttons is-pulled-right">
              <template v-if="editable">
                  <button
                    v-if="page.id"
                    class="button outlined"
                    @click.prevent="setNotEditable"
                  >
                    CANCEL
                  </button>
                  <button
                    class="button is-success"
                    type="submit"
                  >
                    {{submitLabel}}
                  </button>
              </template>

              <template v-else>
                  <b-button
                    :disabled="!page.pageName"
                    id="view-page-btn"
                    class="button is-pulled-right" type="outlined"
                    @click="viewPage"
                  >
                    OPEN LINK
                  </b-button>

                  <b-button
                    class="button is-pulled-right"
                    type="is-success"
                    @click="setEditable()"
                  >
                    EDIT
                  </b-button>
              </template>
            </div>
          </div>
        </form>
      </validation-observer>
    </section>
  </div>
</template>


<script>
import { mapGetters, mapState } from 'vuex';
import { get, isEmpty } from 'lodash';
import {
  uploadLogo as uploadPageLogo,
  create as createPage,
  update as updatePage, getSettings,
} from '@/api/page_partner';
import CoBrandedPageLogoImage from '@/views/CoBrandedPage/components/CoBrandedPageLogoImage.vue';
import CoBrandedPagePageUrl from '@/views/CoBrandedPage/components/CoBrandedPagePageUrl.vue';
import { CO_BRANDED_PAGE_ACCESS, CO_BRANDED_PAGE_ACCESS_MANAGE } from '@/const/privileges';
import { LOAD_PAGES, PARTNER } from '@/store/modules/partner/actions';
import getPreparedPageName from '@/views/CoBrandedPage/utils/getPreparedPageName';

export default {
  name: 'CoBrandedPagePartner',
  components: { CoBrandedPagePageUrl, CoBrandedPageLogoImage },

  data() {
    return {
      CO_BRANDED_PAGE_ACCESS,
      CO_BRANDED_PAGE_ACCESS_MANAGE,
      blobImage: null,
      categoryUrlActually: null,
      editable: false,
      page: {
        id: null,
        pageName: null,
        logoUrl: null,
      },
      params: {
        pageName: null,
        logoUrl: null,
      },
      isPreloading: false,
      currentPageName: null,
    };
  },
  async activated() {
    this.setNotEditable();
    this.isPreloading = true;

    await this.$store.dispatch(`partner/${LOAD_PAGES}`);
    await this.setCategoryUrl();

    this.setDataFromCurrentPage();

    if (!this.page.id) {
      this.setEditable();
    }
    this.isPreloading = false;
  },
  watch: {
    'params.pageName': function paramsPageName(val) {
      if (val) {
        this.params.pageName = val.replace(/[^-a-zA-Z0-9\s]/gui, '');
        this.params.pageName = this.params.pageName.replace(/(\s+)/gui, '-').toLowerCase();
      }
    },
  },
  computed: {
    ...mapState('partner', {
      partnerId: (state) => state.id,
      pages: (state) => state.pages,
      partnerCompanyName: (state) => state.companyName,
      partnerFirstName: (state) => state.firstName,
      partnerLastName: (state) => state.lastName,
    }),
    ...mapGetters(['hasAccess']),
    getCountPages() {
      if (!isEmpty(this.pages)) {
        return this.pages.length;
      }
      return 0;
    },
    submitLabel() {
      return this.page.id ? 'SUBMIT' : 'CREATE';
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    logoAlt() {
      let alt = this.partnerCompanyName;

      if (!alt) {
        alt = `${this.partnerFirstName} ${this.partnerLastName}`;
      }

      return alt;
    },
    pageFullUrl() {
      let fullUrl = '';

      if (this.categoryUrlActually && this.page.pageName) {
        fullUrl = `${this.categoryUrlActually}${this.page.pageName}`;
      }

      return fullUrl;
    },
  },
  methods: {
    setEditable() {
      this.editable = true;
    },
    viewPage() {
      const btn = document.getElementById('view-page-btn');
      if (btn) {
        btn.blur();
      }
      window.open(this.pageFullUrl, '_blank');
    },
    changePage() {
      // page partner second iteration
      // this.isPreloading = true;
      // this.pages.forEach((item) => {
      //   if (item.pageName === this.currentPageName) {
      //     this.setSelectedPage(item);
      //     this.setDataFromCurrentPage(item).finally(() => {
      //       this.isPreloading = false;
      //     });
      //   }
      // });
    },
    setNotEditable() {
      this.blobImage = null;
      this.params.pageName = this.page.pageName;
      this.params.logoUrl = this.page.logoUrl;

      this.editable = false;
    },
    async setCategoryUrl() {
      const response = await getSettings();
      if (response.data) {
        this.categoryUrlActually = response.data.categoryUrl;
      }
    },
    setDataFromCurrentPage() {
      const partnerPage = this.pages ? this.pages[0] : null;

      if (partnerPage) {
        this.page.id = partnerPage.id;
        this.page.pageName = partnerPage.pageName;
        this.page.logoUrl = partnerPage.params.logo;
      } else {
        let pageName = this.partnerCompanyName;

        if (!pageName) {
          pageName = `${this.partnerFirstName} ${this.partnerLastName}`;
        }

        this.page.id = null;
        this.page.pageName = getPreparedPageName(`${pageName}-promo`);
        this.page.logoUrl = null;
      }

      this.params.pageName = this.page.pageName;
      this.params.logoUrl = this.page.logoUrl;
    },
    async submit() {
      this.isPreloading = true;

      try {
        let pageLogoName = this.params.logoUrl;

        if (this.blobImage) {
          const formData = new FormData();
          const file = new File([this.blobImage], 'uploadImage', { type: 'image/png' });
          formData.set('logo', file);
          formData.set('pageId', null);
          const routePageId = this.page.id ? this.page.id : 'null';
          const uploadLogoResponse = await uploadPageLogo(this.partnerId, routePageId, formData);
          pageLogoName = uploadLogoResponse.data.result;
        }

        const params = {};
        params.pageName = this.params.pageName;

        if (this.params.logoUrl === null || this.params.logoUrl !== pageLogoName) {
          params.pageLogoName = pageLogoName;
        }

        const response = await (this.page.id
          ? updatePage(this.partnerId, this.page.id, params)
          : createPage(this.partnerId, params));

        if (response.data.error) {
          this.toast(response.data.message, 'is-danger');
        } else {
          this.$buefy.toast.open({ message: `${this.page.id ? 'Update' : 'Create'} successful`, type: 'is-success', duration: 3000 });
          await this.$store.dispatch(`partner/${LOAD_PAGES}`);
          await this.$store.dispatch(`partner/${PARTNER}`, this.partnerId);
          this.setDataFromCurrentPage();
        }
        this.setNotEditable();
      } catch (error) {
        const isUIErrorMessage = get(error, 'response.data.is_ui_error_message', true);
        if (isUIErrorMessage && error.response.data.error) {
          this.$buefy.toast.open({ message: error.response.data.message, type: 'is-danger' });
        }
      }

      this.isPreloading = false;
    },
  },
};
</script>

<style lang="stylus">
.co-branded-page-view.co-branded-page-view--partner
  .image-section-image-wrapper
    width 200px
    height 60px
    background-color #f2f2f2
    position relative
    &.no-image
      img
        display none
      &:after
        content 'No Image'
        position absolute
        top 50%
        left 50%
        transform translate(-50%, -50%)
</style>
